import { join } from "node:path";
import {
	Agent,
	type AgentMessage,
	type AgentTool,
	convertToLlm,
	createBackgroundPlaceholderText,
	createBackgroundTaskMessage,
	estimateContextTokens,
	type ThinkingLevel,
} from "@kolisachint/hoocode-agent-core";
import { clampThinkingLevel, type Message, type Model, streamSimple } from "@kolisachint/hoocode-ai";
import { getAgentDir } from "../config.js";
import { readConfig as readHooConfig } from "../extensions/core/config.js";
import { AgentSession } from "./agent-session.js";
import { formatNoModelsAvailableMessage } from "./auth-guidance.js";
import { AuthStorage } from "./auth-storage.js";
import { evictSupersededReads } from "./context-gc.js";
import { DEFAULT_THINKING_LEVEL } from "./defaults.js";
import type { ExtensionRunner, LoadExtensionsResult, SessionStartEvent, ToolDefinition } from "./extensions/index.js";
import { ModelRegistry } from "./model-registry.js";
import { defaultModelPerProvider, findInitialModel } from "./model-resolver.js";
import type { ResourceLoader } from "./resource-loader.js";
import { DefaultResourceLoader } from "./resource-loader.js";
import { getDefaultSessionDir, SessionManager } from "./session-manager.js";
import { SettingsManager } from "./settings-manager.js";
import { peekSubagentPool } from "./subagent-pool-instance.js";
import { isInstallTelemetryEnabled } from "./telemetry.js";
import { time } from "./timings.js";
import {
	createBashTool,
	createCodingTools,
	createEditTool,
	createReadOnlyTools,
	createReadTool,
	createWriteTool,
	type ToolName,
} from "./tools/index.js";

export interface CreateAgentSessionOptions {
	/** Working directory for project-local discovery. Default: process.cwd() */
	cwd?: string;
	/** Global config directory. Default: ~/.hoocode/agent */
	agentDir?: string;

	/** Auth storage for credentials. Default: AuthStorage.create(agentDir/auth.json) */
	authStorage?: AuthStorage;
	/** Model registry. Default: ModelRegistry.create(authStorage, agentDir/models.json) */
	modelRegistry?: ModelRegistry;

	/** Model to use. Default: from settings, else first available */
	model?: Model<any>;
	/** Thinking level. Default: from settings, else 'medium' (clamped to model capabilities) */
	thinkingLevel?: ThinkingLevel;
	/** Models available for cycling (Ctrl+P in interactive mode) */
	scopedModels?: Array<{ model: Model<any>; thinkingLevel?: ThinkingLevel }>;

	/**
	 * Optional default tool suppression mode when no explicit allowlist is provided.
	 *
	 * - "all": start with no tools enabled
	 * - "builtin": disable the default built-in tools (read, bash, edit, write)
	 *   but keep extension/custom tools enabled
	 */
	noTools?: "all" | "builtin";
	/**
	 * Optional allowlist of tool names.
	 *
	 * When omitted, hoocode enables the default built-in tools (read, bash, edit, write)
	 * and leaves extension/custom tools enabled unless `noTools` changes that default.
	 * When provided, only the listed tool names are enabled.
	 */
	tools?: string[];
	/**
	 * Optional denylist of tool names, subtracted from whatever set is otherwise
	 * enabled (allowlist or default). Applied to built-in, extension, and custom tools.
	 */
	disallowedTools?: string[];
	/**
	 * Enable the built-in `webfetch` + `websearch` tools, which are defined but
	 * inactive by default. Ignored when an explicit `tools` allowlist is provided
	 * (list `webfetch`/`websearch` there instead). Network access is still gated
	 * per call and filtered by `.webtoolsignore`.
	 */
	enableWebTools?: boolean;
	/**
	 * Enable the semantic index layer for the built-in `SearchCodebase` tool (ranked
	 * lexical + semantic retrieval, rank-fused). The search tool is active by
	 * default; this flag only toggles the optional embedding index. When false,
	 * search degrades to lexical-only. Ignored when an explicit `tools` allowlist
	 * is provided (list it there instead).
	 */
	enableSemanticIndex?: boolean;
	/** Custom tools to register (in addition to built-in tools). */
	customTools?: ToolDefinition[];
	/**
	 * Replace the built-in base tools entirely (the light preset uses this to
	 * swap in short-schema read/write/edit/bash variants). Keys become the
	 * default active tool set when no explicit `tools` allowlist is provided.
	 */
	baseToolsOverride?: Record<string, AgentTool>;

	/** Resource loader. When omitted, DefaultResourceLoader is used. */
	resourceLoader?: ResourceLoader;

	/** Session manager. Default: SessionManager.create(cwd) */
	sessionManager?: SessionManager;

	/** Settings manager. Default: SettingsManager.create(cwd, agentDir) */
	settingsManager?: SettingsManager;
	/** Session start event metadata for extension runtime startup. */
	sessionStartEvent?: SessionStartEvent;
}

/** Result from createAgentSession */
export interface CreateAgentSessionResult {
	/** The created session */
	session: AgentSession;
	/** Extensions result (for UI context setup in interactive mode) */
	extensionsResult: LoadExtensionsResult;
	/** Warning if session was restored with a different model than saved */
	modelFallbackMessage?: string;
}

// Re-exports

export * from "./agent-session-runtime.js";
export type { ExtensionFactory } from "./extensions/index.js";
export type { PromptTemplate } from "./prompt-templates.js";

export {
	// Tool factories (for custom cwd)
	createCodingTools,
	createReadOnlyTools,
	createReadTool,
	createBashTool,
	createEditTool,
	createWriteTool,
};

// Helper Functions

function getDefaultAgentDir(): string {
	return getAgentDir();
}

function getAttributionHeaders(
	model: Model<any>,
	settingsManager: SettingsManager,
): Record<string, string> | undefined {
	if (!isInstallTelemetryEnabled(settingsManager)) {
		return undefined;
	}

	if (model.provider === "openrouter" || model.baseUrl.includes("openrouter.ai")) {
		return {
			"HTTP-Referer": "https://github.com/kolisachint/hoocode",
			"X-OpenRouter-Title": "hoocode",
			"X-OpenRouter-Categories": "cli-agent",
		};
	}

	return undefined;
}

/**
 * Create an AgentSession with the specified options.
 *
 * @example
 * ```typescript
 * // Minimal - uses defaults
 * const { session } = await createAgentSession();
 *
 * // With explicit model
 * import { getModel } from '@kolisachint/hoocode-ai';
 * const { session } = await createAgentSession({
 *   model: getModel('anthropic', 'claude-opus-4-5'),
 *   thinkingLevel: 'high',
 * });
 *
 * // Continue previous session
 * const { session, modelFallbackMessage } = await createAgentSession({
 *   continueSession: true,
 * });
 *
 * // Full control
 * const loader = new DefaultResourceLoader({
 *   cwd: process.cwd(),
 *   agentDir: getAgentDir(),
 *   settingsManager: SettingsManager.create(),
 * });
 * await loader.reload();
 * const { session } = await createAgentSession({
 *   model: myModel,
 *   tools: [readTool, bashTool],
 *   resourceLoader: loader,
 *   sessionManager: SessionManager.inMemory(),
 * });
 * ```
 */
export async function createAgentSession(options: CreateAgentSessionOptions = {}): Promise<CreateAgentSessionResult> {
	const cwd = options.cwd ?? options.sessionManager?.getCwd() ?? process.cwd();
	const agentDir = options.agentDir ?? getDefaultAgentDir();
	let resourceLoader = options.resourceLoader;

	// Use provided or create AuthStorage and ModelRegistry
	const authPath = options.agentDir ? join(agentDir, "auth.json") : undefined;
	const modelsPath = options.agentDir ? join(agentDir, "models.json") : undefined;
	const authStorage = options.authStorage ?? AuthStorage.create(authPath);
	const modelRegistry = options.modelRegistry ?? ModelRegistry.create(authStorage, modelsPath);

	const settingsManager = options.settingsManager ?? SettingsManager.create(cwd, agentDir);
	const sessionManager = options.sessionManager ?? SessionManager.create(cwd, getDefaultSessionDir(cwd, agentDir));

	if (!resourceLoader) {
		resourceLoader = new DefaultResourceLoader({ cwd, agentDir, settingsManager });
		await resourceLoader.reload();
		time("resourceLoader.reload");
	}

	// Check if session has existing data to restore
	const existingSession = sessionManager.buildSessionContext();
	const hasExistingSession = existingSession.messages.length > 0;
	const hasThinkingEntry = sessionManager.getBranch().some((entry) => entry.type === "thinking_level_change");

	let model = options.model;
	let modelFallbackMessage: string | undefined;

	// If session has data, try to restore model from it
	if (!model && hasExistingSession && existingSession.model) {
		const restoredModel = modelRegistry.find(existingSession.model.provider, existingSession.model.modelId);
		if (restoredModel && modelRegistry.hasConfiguredAuth(restoredModel)) {
			model = restoredModel;
		}
		if (!model) {
			modelFallbackMessage = `Could not restore model ${existingSession.model.provider}/${existingSession.model.modelId}`;
		}
	}

	// If still no model, use findInitialModel (checks settings default, then provider defaults)
	if (!model) {
		// The pi-layer settings.json default wins; when it is unset, fall back to the
		// hoo-config.json `llm.default_provider` so the seeded/user default actually
		// takes effect. findInitialModel only honours it if that provider has auth.
		const hooLlm = readHooConfig().llm;
		const defaultProvider = settingsManager.getDefaultProvider() ?? hooLlm?.default_provider;
		const defaultModelId =
			settingsManager.getDefaultModel() ??
			(settingsManager.getDefaultProvider()
				? undefined
				: (hooLlm?.default_model ??
					(defaultProvider && defaultProvider in defaultModelPerProvider
						? defaultModelPerProvider[defaultProvider as keyof typeof defaultModelPerProvider]
						: undefined)));
		const result = await findInitialModel({
			scopedModels: [],
			isContinuing: hasExistingSession,
			defaultProvider,
			defaultModelId,
			defaultThinkingLevel: settingsManager.getDefaultThinkingLevel(),
			modelRegistry,
		});
		model = result.model;
		if (!model) {
			modelFallbackMessage = formatNoModelsAvailableMessage();
		} else if (modelFallbackMessage) {
			modelFallbackMessage += `. Using ${model.provider}/${model.id}`;
		}
	}

	let thinkingLevel = options.thinkingLevel;

	// If session has data, restore thinking level from it
	if (thinkingLevel === undefined && hasExistingSession) {
		thinkingLevel = hasThinkingEntry
			? (existingSession.thinkingLevel as ThinkingLevel)
			: (settingsManager.getDefaultThinkingLevel() ?? DEFAULT_THINKING_LEVEL);
	}

	// Fall back to settings default
	if (thinkingLevel === undefined) {
		thinkingLevel = settingsManager.getDefaultThinkingLevel() ?? DEFAULT_THINKING_LEVEL;
	}

	// Clamp to model capabilities
	if (!model) {
		thinkingLevel = "off";
	} else {
		thinkingLevel = clampThinkingLevel(model, thinkingLevel) as ThinkingLevel;
	}

	// `SearchCodebase` is always active: it answers "find where X lives" with ranked
	// results and degrades to exact-text lexical retrieval when no semantic
	// index is present, so it needs no binary to be useful. The
	// `enableSemanticIndex` flag only controls whether the semantic index is
	// built and fused in (see main.ts) — not whether the tool exists.
	const defaultActiveToolNames: ToolName[] = ["read", "bash", "edit", "write", "SearchCodebase"];
	// Web tools are registered as base tools but inactive by default; opt-in adds
	// them to the default active set. An explicit allowlist (`tools`) takes over
	// fully, so callers must list them there to enable in that mode.
	const optInActiveToolNames: ToolName[] = [
		...(options.enableWebTools ? ["webfetch", "websearch"] : []),
	] as ToolName[];
	const allowedToolNames = options.tools ?? (options.noTools === "all" ? [] : undefined);
	const initialActiveToolNames: string[] = options.tools
		? [...options.tools]
		: options.noTools
			? []
			: [...defaultActiveToolNames, ...optInActiveToolNames];

	let agent: Agent;

	// Create convertToLlm wrapper that filters images if blockImages is enabled (defense-in-depth)
	const convertToLlmWithBlockImages = (messages: AgentMessage[]): Message[] => {
		const converted = convertToLlm(messages);
		// Check setting dynamically so mid-session changes take effect
		if (!settingsManager.getBlockImages()) {
			return converted;
		}
		// Filter out ImageContent from all messages, replacing with text placeholder
		return converted.map((msg) => {
			if (msg.role === "user" || msg.role === "toolResult") {
				const content = msg.content;
				if (Array.isArray(content)) {
					const hasImages = content.some((c) => c.type === "image");
					if (hasImages) {
						const filteredContent = content
							.map((c) =>
								c.type === "image" ? { type: "text" as const, text: "Image reading is disabled." } : c,
							)
							.filter(
								(c, i, arr) =>
									// Dedupe consecutive "Image reading is disabled." texts
									!(
										c.type === "text" &&
										c.text === "Image reading is disabled." &&
										i > 0 &&
										arr[i - 1].type === "text" &&
										(arr[i - 1] as { type: "text"; text: string }).text === "Image reading is disabled."
									),
							);
						return { ...msg, content: filteredContent };
					}
				}
			}
			return msg;
		});
	};

	const extensionRunnerRef: { current?: ExtensionRunner } = {};

	// Token-budget pressure for context GC: the fraction of the active model's
	// context window in use, measured from the real usage on the outgoing
	// message copy. It latches to a high-water mark so that our own evictions —
	// which shrink the next turn's measured usage — cannot oscillate a message
	// in and out of the transcript and thrash the provider's prefix cache. A
	// large drop (compaction, fork) resets the latch to the new, smaller size.
	let budgetPressureHighWater = 0;
	const getBudgetPressure = (contextMessages: AgentMessage[]): number => {
		const contextWindow = agent.state.model?.contextWindow ?? 0;
		if (contextWindow <= 0) return 0;
		const gauge = Math.min(estimateContextTokens(contextMessages).tokens / contextWindow, 1);
		if (gauge > budgetPressureHighWater) {
			budgetPressureHighWater = gauge; // rising usage — track it
		} else if (gauge < budgetPressureHighWater - 0.15) {
			budgetPressureHighWater = gauge; // context collapsed — reset the latch
		}
		return budgetPressureHighWater;
	};

	agent = new Agent({
		initialState: {
			systemPrompt: "",
			model,
			thinkingLevel,
			tools: [],
		},
		convertToLlm: convertToLlmWithBlockImages,
		createBackgroundResultMessage: createBackgroundTaskMessage,
		createBackgroundPlaceholder: (toolCall) => createBackgroundPlaceholderText(toolCall),
		// Report in-process background tool load (e.g. background MCP tools) to the
		// subagent lifeguard so it widens its heartbeat/timeout tolerance for
		// concurrently-monitored subagents. Peek (don't create) the pool: background
		// tools can run before any subagent is ever dispatched.
		onBackgroundTaskCountChange: (count) => peekSubagentPool()?.setExternalLoad(count),
		streamFn: async (model, context, options) => {
			const auth = await modelRegistry.getApiKeyAndHeaders(model);
			if (!auth.ok) {
				throw new Error(auth.error);
			}
			const providerRetrySettings = settingsManager.getProviderRetrySettings();
			const attributionHeaders = getAttributionHeaders(model, settingsManager);
			return streamSimple(model, context, {
				...options,
				apiKey: auth.apiKey,
				timeoutMs: options?.timeoutMs ?? providerRetrySettings.timeoutMs,
				maxRetries: options?.maxRetries ?? providerRetrySettings.maxRetries,
				maxRetryDelayMs: options?.maxRetryDelayMs ?? providerRetrySettings.maxRetryDelayMs,
				headers:
					attributionHeaders || auth.headers || options?.headers
						? { ...attributionHeaders, ...auth.headers, ...options?.headers }
						: undefined,
			});
		},
		onPayload: async (payload, _model) => {
			const runner = extensionRunnerRef.current;
			if (!runner?.hasHandlers("before_provider_request")) {
				return payload;
			}
			return runner.emitBeforeProviderRequest(payload);
		},
		onResponse: async (response, _model) => {
			const runner = extensionRunnerRef.current;
			if (!runner?.hasHandlers("after_provider_response")) {
				return;
			}
			await runner.emit({
				type: "after_provider_response",
				status: response.status,
				headers: response.headers,
			});
		},
		sessionId: sessionManager.getSessionId(),
		transformContext: async (messages) => {
			const runner = extensionRunnerRef.current;
			const transformed = runner ? await runner.emitContext(messages) : messages;
			if (!settingsManager.getContextGcEnabled()) return transformed;
			return evictSupersededReads(transformed, { cwd, budgetPressure: getBudgetPressure(transformed) });
		},
		steeringMode: settingsManager.getSteeringMode(),
		followUpMode: settingsManager.getFollowUpMode(),
		transport: settingsManager.getTransport(),
		thinkingBudgets: settingsManager.getThinkingBudgets(),
		thinkingDisplay: settingsManager.getThinkingDisplay(),
		maxRetryDelayMs: settingsManager.getProviderRetrySettings().maxRetryDelayMs,
	});

	// Restore messages if session has existing data
	if (hasExistingSession) {
		agent.state.messages = existingSession.messages;
		if (!hasThinkingEntry) {
			sessionManager.appendThinkingLevelChange(thinkingLevel);
		}
	} else {
		// Save initial model and thinking level for new sessions so they can be restored on resume
		if (model) {
			sessionManager.appendModelChange(model.provider, model.id);
		}
		sessionManager.appendThinkingLevelChange(thinkingLevel);
	}

	const session = new AgentSession({
		agent,
		sessionManager,
		settingsManager,
		cwd,
		scopedModels: options.scopedModels,
		resourceLoader,
		customTools: options.customTools,
		baseToolsOverride: options.baseToolsOverride,
		modelRegistry,
		initialActiveToolNames,
		allowedToolNames,
		disallowedToolNames: options.disallowedTools,
		extensionRunnerRef,
		sessionStartEvent: options.sessionStartEvent,
	});
	const extensionsResult = resourceLoader.getExtensions();

	return {
		session,
		extensionsResult,
		modelFallbackMessage,
	};
}
